import { ICommentDto, ICommentReaction, SpecialDIIds } from "@kuika/kuika-cl-model";
import React from "react";
import { IComments } from "../../../../models/interface/comment";
import { DirectoryMember } from "../../../management/board/people-directory";
import KuikaAvatar, { getIdentitySeed } from "../../../ui-kit/kuika-avatar/kuika-avatar";
import { IViewportBox, IVisibleBounds, threadKey } from "./comments-panel-types";

// Stateless helpers + constants for the comments panel, split out of the (large) component so the
// pure logic can be read and unit-tested on its own.

// ─── Constants ─────────────────────────────────────────────────────────────────

// Page-scope threads use the root display-item id ("-1"): list-only, no canvas pin.
export const PAGE_SCOPE_KUIKA_ID: string = SpecialDIIds.rootDI.toString();

export const EMOJI_SET: string[] = [
  "👍",
  "👎",
  "😄",
  "🎉",
  "😕",
  "❤️",
  "🚀",
  "👀",
  "✅",
  "❌",
  "🔥",
  "⚠️",
  "💡",
  "🙏",
  "😂",
  "🤔"
];

// ─── Helpers ────────────────────────────────────────────────────────────────────

export function formatRelativeTime(date: Date): string {
  const now: number = Date.now();
  const diff: number = Math.floor((now - new Date(date).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return new Date(date).toLocaleDateString("en-US", { day: "numeric", month: "short" });
}

export function getUserInitials(
  firstName: string | undefined,
  lastName: string | undefined,
  username: string | undefined
): string {
  const fn: string = firstName ?? "";
  const ln: string = lastName ?? "";
  if (fn || ln) return `${fn.charAt(0)}${ln.charAt(0)}`.toUpperCase();
  return (username ?? "?").charAt(0).toUpperCase();
}

/**
 * The commenter, shared by the thread cards and the @mention popup.
 *
 * This panel used to keep its own six colours — four of them hardcoded hexes — and its own
 * hash to pick between them, so the same colleague was one colour here and another in the
 * profile dialog. It draws the shared avatar now, which knows all ten and picks the same
 * one everywhere.
 */
export function renderAvatar(
  userId: string,
  firstName: string | undefined,
  lastName: string | undefined,
  username: string | undefined,
  size: "sm" | "md" = "md"
): React.ReactNode {
  return (
    <KuikaAvatar
      size={size === "sm" ? 20 : 24}
      seed={getIdentitySeed({ firstName, lastName, username })}
      className="comments-panel__av"
    >
      {getUserInitials(firstName, lastName, username)}
    </KuikaAvatar>
  );
}

export function convertCommentsDtoToInterface(data: ICommentDto[]): IComments[] {
  const result: IComments[] = [];
  data.forEach((d: ICommentDto) => {
    // Group by threadId when present (multiple threads per component); legacy rows have
    // no threadId and fall back to grouping by kuikaId (one thread per component).
    const groupKey: string = d.threadId ?? d.kuikaId;
    const alreadyAdded: IComments | undefined = result.find((x: IComments) => (x.threadId ?? x.kuikaId) === groupKey);
    if (alreadyAdded) {
      alreadyAdded.comments.push(d);
      if (new Date(alreadyAdded.latestCreatedAt).getTime() < new Date(d.createdAt).getTime()) {
        alreadyAdded.latestCreatedAt = d.createdAt;
      }
    } else {
      const obj: IComments = {
        kuikaId: d.kuikaId,
        order: d.order,
        comments: [d],
        latestCreatedAt: d.createdAt,
        threadId: d.threadId
      };
      result.push(obj);
    }
  });
  result.forEach((c: IComments) => {
    c.comments.sort(
      (a: ICommentDto, b: ICommentDto) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    // Thread identity + pin anchor come from the root (earliest) comment.
    const root: ICommentDto | undefined = c.comments[0];
    c.threadId = root?.threadId;
    c.anchorFx = root?.anchorX;
    c.anchorFy = root?.anchorY;
    c.order = root?.order ?? c.order;
  });
  result.sort(
    (a: IComments, b: IComments) => new Date(b.latestCreatedAt).getTime() - new Date(a.latestCreatedAt).getTime()
  );
  return result;
}

// Slack-style reactor summary: "You reacted", "You and Ali reacted", "You, Ali and Mehmet reacted".
export function formatReactors(names: string[]): string {
  if (!names.length) return "";
  if (names.length === 1) return `${names[0]} reacted`;
  const head: string = names.slice(0, -1).join(", ");
  return `${head} and ${names[names.length - 1]} reacted`;
}

// Optimistic per-(user, emoji) toggle: a user may hold several emojis at once and toggling one affects only that one (a slow reload racing a second emoji used to drop the first).
export function toggleReactionOnComment(comment: ICommentDto, emoji: string, currentUserId: string): ICommentDto {
  const reactions: ICommentReaction[] = comment.reactions ?? [];
  const existing: ICommentReaction | undefined = reactions.find((r: ICommentReaction) => r.emoji === emoji);
  let next: ICommentReaction[];
  if (!existing) {
    next = [...reactions, { emoji, count: 1, reactedByCurrentUser: true, reactedUserIds: [currentUserId] }];
  } else if (existing.reactedByCurrentUser) {
    const count: number = existing.count - 1;
    const remaining: string[] = (existing.reactedUserIds ?? []).filter((id: string) => id !== currentUserId);
    next =
      count <= 0
        ? reactions.filter((r: ICommentReaction) => r.emoji !== emoji)
        : reactions.map((r: ICommentReaction) =>
            r.emoji === emoji ? { ...r, count, reactedByCurrentUser: false, reactedUserIds: remaining } : r
          );
  } else {
    next = reactions.map((r: ICommentReaction) =>
      r.emoji === emoji
        ? {
            ...r,
            count: r.count + 1,
            reactedByCurrentUser: true,
            reactedUserIds: [...(r.reactedUserIds ?? []), currentUserId]
          }
        : r
    );
  }
  return { ...comment, reactions: next };
}

export function highlightMentions(text: string, members: DirectoryMember[]): React.ReactNode {
  if (!members.length || !text.includes("@")) return text;
  // Sort names longest-first to prevent partial matches shadowing full names.
  const sorted: DirectoryMember[] = [...members].sort(
    (a: DirectoryMember, b: DirectoryMember) => b.name.length - a.name.length
  );
  const escaped: string[] = sorted.map((m: DirectoryMember) => m.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const pattern: RegExp = new RegExp(`@(${escaped.join("|")})`, "g");
  const parts: string[] = text.split(pattern);
  const nodes: React.ReactNode[] = [];
  for (let i: number = 0; i < parts.length; i++) {
    const part: string = parts[i];
    if (i % 2 === 1) {
      nodes.push(
        <span key={i} className="comments-panel__mention">
          @{part}
        </span>
      );
    } else {
      if (part) nodes.push(part);
    }
  }
  return nodes.length ? nodes : text;
}

export function extractMentionToken(text: string, cursorPos: number): string | null {
  const before: string = text.slice(0, cursorPos);
  const match: RegExpMatchArray | null = before.match(/@([^\s@]*)$/);
  return match ? match[1] : null;
}

/**
 * Clip a viewport box to the canvas's visible edges, or null when the two don't overlap at all.
 *
 * The canvas iframe scrolls its own content, so a component taller (or wider) than the visible canvas
 * reports a rect that runs past it — painting the hover highlight over the designer chrome below the
 * canvas. Pins already skip anchors outside these bounds; the highlight has to respect the same edges.
 */
export function clipBoxToBounds(box: IViewportBox, bounds: IVisibleBounds): IViewportBox | null {
  const left: number = Math.max(box.x, bounds.left);
  const top: number = Math.max(box.y, bounds.top);
  const right: number = Math.min(box.x + box.width, bounds.right);
  const bottom: number = Math.min(box.y + box.height, bounds.bottom);
  // Touching edges (zero width/height) count as outside: there is nothing to draw.
  if (right <= left || bottom <= top) return null;
  return { x: left, y: top, width: right - left, height: bottom - top };
}

// Stable kuikaId → display number map, keyed from the full unfiltered thread list sorted by `order`
// ascending, so card badges and canvas pins always show the same number regardless of active filter.
export function buildPinNumbers(threads: IComments[]): Map<string, number> {
  // Only component-anchored threads get a canvas pin (and thus a number). Page-scope threads are
  // list-only, so they're excluded — keeping pin numbers contiguous 1..N and matching the canvas.
  const sorted: IComments[] = [...threads]
    .filter((t: IComments) => t.kuikaId !== PAGE_SCOPE_KUIKA_ID)
    .sort((a: IComments, b: IComments) => a.order - b.order);
  const map: Map<string, number> = new Map<string, number>();
  sorted.forEach((t: IComments, i: number) => {
    map.set(threadKey(t), i + 1);
  });
  return map;
}

// The @ tab is an inbox, not an archive: a mention is listed only while its thread is still open,
// so resolving a thread takes it out of the tab as well. Shared by the tab's filter, the tab's
// count, and the filter the panel falls back to when it reveals a thread, so the three can never
// disagree about what "@" holds.
export function isOpenMentionOf(thread: IComments, currentUserId: string | undefined): boolean {
  if (!currentUserId) return false;
  const isResolved: boolean = thread.comments[0]?.isResolved === true;
  if (isResolved) return false;
  return thread.comments.some((c: ICommentDto) => c.mentionedUserIds?.includes(currentUserId));
}
